/**
 * SubEstadosConfig -- Catalogo de insumos con sub-estados para verificacion de inventario.
 *
 * V1 (PANEL-CONTEO-INTELIGENTE-V1-001): hard-coded por substring del nombre.
 * V2 (PANEL-CONTEO-INTELIGENTE-V2-MEJORAS-V1-001): override por business en localStorage.
 * V3 (PANEL-INVENTARIO-CONFIG-PERSIST-BD-V1-001): config en BD nexo_business.insumos.sub_estados_config (jsonb).
 *
 * V3 source-of-truth: BD via API. Cache in-memory por business + fallback localStorage offline.
 * Reading: `InventarioApiClient.getInsumosWithConfig(bizId)` retorna lista insumos con campo
 *   `sub_estados_config` (jsonb) directamente desde BD. Panel pasa esos items al constructor
 *   de allInsumos en InventarioView (NO usa getInsumoConfig per-nombre).
 * Writing: `InventarioApiClient.patchInsumoConfig(bizId, insumoId, {sub_estados_config})`.
 *
 * Las funciones sync `getInsumoConfig(bizId, nombre)` / `saveInsumoConfig(bizId, nombre, config)`
 * se mantienen como FALLBACK localStorage cuando API no disponible (offline o endpoint pendiente).
 *
 * @module presentation/components/SubEstadosConfig
 */

const PATRONES_DEFAULT = [
  { match: 'pollo', estados: [{ label: 'Congelado', tipo: 'simple' }, { label: 'Procesado', tipo: 'simple' }] },
  { match: 'arroz', estados: [{ label: 'Crudo', tipo: 'simple' }, { label: 'Cocido', tipo: 'simple' }] }
];

const STORAGE_PREFIX = 'nx_inv_subestados_config_';

function storageKey(bizId) {
  return STORAGE_PREFIX + (bizId || 'default');
}

function readAllConfig(bizId) {
  try {
    const raw = localStorage.getItem(storageKey(bizId));
    if (!raw) return { insumos: {} };
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? { insumos: parsed.insumos || {} } : { insumos: {} };
  } catch { return { insumos: {} }; }
}

function writeAllConfig(bizId, all) {
  try {
    localStorage.setItem(storageKey(bizId), JSON.stringify(all));
    // FIXES-V1 T2 (REACTIVIDAD-NEGOCIO): BroadcastChannel para sync cross-pestana en mismo browser.
    // El `storage` event se dispara automaticamente en OTRAS pestanas; BroadcastChannel cubre
    // mensaje custom con detail (bizId + nombre afectado).
    if (typeof BroadcastChannel !== 'undefined') {
      try {
        const ch = new BroadcastChannel('nx-inv-config');
        ch.postMessage({ type: 'config-changed', bizId, ts: Date.now() });
        ch.close();
      } catch { /* no-op */ }
    }
    return true;
  } catch { return false; }
}

function configFromPatronDefault(nombre) {
  if (!nombre) return null;
  const n = String(nombre).toLowerCase();
  for (const p of PATRONES_DEFAULT) {
    if (n.includes(p.match)) {
      return { enabled: true, estados: p.estados.map(e => ({ ...e })) };
    }
  }
  return null;
}

/**
 * Devuelve la config del insumo (override localStorage > patron default > null).
 * @param {string} bizId
 * @param {string} nombre
 * @returns {{enabled:boolean, estados:Array<{label:string,tipo:string}>} | null}
 */
export function getInsumoConfig(bizId, nombre) {
  if (!nombre) return null;
  const all = readAllConfig(bizId);
  const override = all.insumos?.[nombre];
  if (override) {
    if (!override.enabled) return null; // explicitamente desactivado
    return {
      enabled: true,
      estados: (override.estados || []).map(e => ({
        label: e.label,
        tipo: e.tipo || 'simple',
        ...(e.tipo === 'porcionado' && e.tamano !== undefined ? { tamano: e.tamano } : {})
      }))
    };
  }
  return configFromPatronDefault(nombre);
}

/**
 * Devuelve el tamano predeterminado de un estado porcionado, o null si no aplica.
 * T6: tamano vive en config, no en cada fila.
 */
export function getEstadoTamano(bizId, nombre, label) {
  const cfg = getInsumoConfig(bizId, nombre);
  if (!cfg) return null;
  const e = cfg.estados.find(x => x.label === label);
  if (!e || e.tipo !== 'porcionado') return null;
  return (e.tamano !== undefined && e.tamano !== null) ? parseFloat(e.tamano) : null;
}

/**
 * Conveniencia: devuelve solo los labels de los estados (compatible V1).
 * @param {string} nombre - O `(bizId, nombre)` para override por business
 * @param {string} [maybeNombre]
 * @returns {string[] | null}
 */
export function getSubEstados(nombreOrBizId, maybeNombre) {
  // Soporte llamada V1 con solo nombre (asume bizId='default')
  const bizId = maybeNombre !== undefined ? nombreOrBizId : 'default';
  const nombre = maybeNombre !== undefined ? maybeNombre : nombreOrBizId;
  const cfg = getInsumoConfig(bizId, nombre);
  return cfg ? cfg.estados.map(e => e.label) : null;
}

/**
 * Devuelve el tipo del sub-estado (simple | porcionado) o 'simple' como default.
 */
export function getEstadoTipo(bizId, nombre, label) {
  const cfg = getInsumoConfig(bizId, nombre);
  if (!cfg) return 'simple';
  const e = cfg.estados.find(x => x.label === label);
  return e?.tipo || 'simple';
}

export function hasSubEstados(nombreOrBizId, maybeNombre) {
  return getSubEstados(nombreOrBizId, maybeNombre) !== null;
}

/**
 * Suma valores de sub_conteos. Acepta tanto formato V1 (num plano)
 * como formato V2/V3 ({filas: [{cantidad,tamano?}], total: number}).
 */
export function sumarSubConteos(subConteos) {
  if (!subConteos) return 0;
  let total = 0;
  for (const v of Object.values(subConteos)) {
    if (v === null || v === undefined) continue;
    if (typeof v === 'object' && 'total' in v) {
      total += parseFloat(v.total) || 0;
    } else if (typeof v === 'object' && Array.isArray(v.filas)) {
      // Sin total cacheado: sumar filas
      for (const f of v.filas) {
        if (f && typeof f === 'object') {
          const cant = parseFloat(f.cantidad) || 0;
          const tam = parseFloat(f.tamano) || 1;
          // Si tipo simple (sin tamano), tam=1 y subtotal=cant
          total += cant * tam;
        }
      }
    } else if (!isNaN(parseFloat(v))) {
      total += parseFloat(v);
    }
  }
  return total;
}

/**
 * Inicializa sub_conteos vacio formato V3 (T3+T4):
 *   { [label]: { filas: [], total: 0, tipo: 'simple'|'porcionado' } }
 * @param {Array<string|{label,tipo}>} subEstados
 */
export function initSubConteos(subEstados) {
  if (!subEstados || subEstados.length === 0) return {};
  const result = {};
  for (const e of subEstados) {
    const label = typeof e === 'string' ? e : e.label;
    const tipo = (typeof e === 'object' && e.tipo) ? e.tipo : 'simple';
    result[label] = { filas: [], total: 0, tipo };
  }
  return result;
}

/**
 * ¿EL PORCIONADO MULTIPLICA POR EL TAMANO? (CONTEO-PORCIONADO-UNIDAD-T1, Ares #27157; medido por D #27152/#27158)
 *
 * Solo si el insumo se lleva en PESO o VOLUMEN: 15 porciones de 250 g de un insumo en gramos son 3.750 g. Si se lleva en
 * UNIDADES (las porciones mismas: en EC el cerdo de 250 g, el de 60 g y los camarones, teorico 11 / 69 / 54), 15
 * porciones son 15: multiplicar guardaba 3.750 y fabricaba un sobrante de +3.735 que al dia siguiente salia como dano.
 * Medido en nexo_business.insumos el 14-oct: las 57 filas dicen unidad 'und' y unidad_conteo vacio, asi que la regla
 * mira la unidad del insumo con una lista EXPLICITA de peso/volumen (la misma idea que ComprasView: calcularDestinoPorcionado
 * solo calcula porciones con unidad de peso). Sin unidad, o 'und', o cualquier otra: suma. El tamano sigue a la vista.
 */
const UNIDADES_DE_PESO_O_VOLUMEN = ['g', 'gr', 'grs', 'gramo', 'gramos', 'kg', 'kgs', 'kilo', 'kilos', 'kilogramo', 'kilogramos',
  'lb', 'lbs', 'libra', 'libras', 'ml', 'mililitro', 'mililitros', 'l', 'lt', 'lts', 'litro', 'litros'];
export function porcionadoMultiplica(unidad) {
  const u = String(unidad ?? '').trim().toLowerCase().replace(/\.$/, '');
  return UNIDADES_DE_PESO_O_VOLUMEN.includes(u);
}

/**
 * Calcula el subtotal de una fila.
 * Simple: cantidad. Porcionado: tamano (config | snapshot fila) * cantidad SI el insumo va en peso/volumen
 * (porcionadoMultiplica); si va en unidades, la cantidad (son porciones).
 *
 * T6: tamano se lee del config por defecto. Si la fila tiene `tamano` legacy
 * (drafts pre-T6), se usa como snapshot para retro-compatibilidad.
 *
 * @param {object} fila
 * @param {string} tipo
 * @param {number|null} [tamanoConfig] - tamano del config (preferido sobre fila.tamano)
 * @param {string} [unidad] - la unidad del insumo; sin ella el porcionado suma (no multiplica)
 */
export function subtotalFila(fila, tipo, tamanoConfig, unidad) {
  if (!fila) return 0;
  const cant = parseFloat(fila.cantidad) || 0;
  if (tipo === 'porcionado' && porcionadoMultiplica(unidad)) {
    let tam = 0;
    if (tamanoConfig !== undefined && tamanoConfig !== null) {
      tam = parseFloat(tamanoConfig) || 0;
    } else if (fila.tamano !== undefined && fila.tamano !== null) {
      tam = parseFloat(fila.tamano) || 0;
    }
    return tam * cant;
  }
  return cant;
}

/**
 * Recalcula total de un estado a partir de sus filas.
 * Si tamanoConfig provisto, se usa para porcionado en lugar de fila.tamano.
 */
export function totalEstado(estado, tamanoConfig, unidad) {
  if (!estado || !Array.isArray(estado.filas)) return 0;
  return estado.filas.reduce((s, f) => s + subtotalFila(f, estado.tipo, tamanoConfig, unidad), 0);
}

/**
 * Persistir config insumo (toggle on/off, lista estados, tipo).
 * Pass null para resetear al default por patron.
 */
export function saveInsumoConfig(bizId, nombre, config) {
  if (!nombre) return false;
  const all = readAllConfig(bizId);
  if (config === null) {
    delete all.insumos[nombre];
  } else {
    all.insumos[nombre] = {
      enabled: config.enabled !== false,
      estados: (config.estados || []).map(e => {
        const out = { label: e.label, tipo: e.tipo || 'simple' };
        // T6: persistir tamano solo cuando tipo=porcionado
        if (out.tipo === 'porcionado' && e.tamano !== undefined && e.tamano !== null && e.tamano !== '') {
          const n = parseFloat(e.tamano);
          if (!isNaN(n) && n > 0) out.tamano = n;
        }
        return out;
      })
    };
  }
  return writeAllConfig(bizId, all);
}

export function removeInsumoConfig(bizId, nombre) {
  return saveInsumoConfig(bizId, nombre, null);
}

/**
 * Lista todos los overrides explicitos del business (sin patron default).
 */
export function listConfigOverrides(bizId) {
  return readAllConfig(bizId).insumos || {};
}

/**
 * Devuelve la lista de patrones default (pollo, arroz) - util para UI mostrar
 * que estos son auto y se pueden overridear.
 */
export function getPatronesDefault() {
  return PATRONES_DEFAULT.map(p => ({ match: p.match, estados: p.estados.map(e => ({ ...e })) }));
}

/**
 * V3 helper: normaliza la config (de BD via API) al mismo shape que retorna
 * `getInsumoConfig` localStorage. Esto permite que el codigo de render
 * (InventarioView) trate ambas fuentes (BD/localStorage/patron) identicamente.
 *
 * Contrato API confirmado bit #1292 (Argy-A T1):
 * - GET response usa **camelCase**: `subEstadosConfig` (JsonPropertyName via System.Text.Json)
 * - PATCH body usa **snake_case**: `sub_estados_config`
 * - Esta funcion soporta AMBOS keys defensivamente para retro-compat.
 *
 * Si el insumo NO tiene config en BD (campo null), se cae al fallback patron default
 * por nombre. Si la config en BD esta enabled=false, retorna null (sin sub-estados).
 *
 * @param {object} insumo - { id, nombre, subEstadosConfig | sub_estados_config: {enabled, estados}|null }
 * @returns {{enabled:boolean, estados:Array<{label,tipo,tamano?}>} | null}
 */
export function normalizeInsumoConfigFromApi(insumo) {
  if (!insumo) return null;
  // Soporta camelCase (response API) y snake_case (defensive)
  const cfg = insumo.subEstadosConfig ?? insumo.sub_estados_config;
  if (cfg === null || cfg === undefined) {
    // Sin override en BD -> fallback patron default por nombre
    return configFromPatronDefault(insumo.nombre);
  }
  if (cfg && cfg.enabled === false) return null;
  return {
    enabled: true,
    estados: (cfg.estados || []).map(e => ({
      label: e.label,
      tipo: e.tipo || 'simple',
      ...(e.tipo === 'porcionado' && e.tamano !== undefined && e.tamano !== null ? { tamano: parseFloat(e.tamano) } : {})
    }))
  };
}
